import { useEffect, useMemo } from "react";

import AmountOdometer from "../../Components/Others/amount-odometer";

import renderAvailableBalance from "../../Organisms/Modals/Banking/available-balance";
import renderCurrentBalance from "../../Organisms/Modals/Banking/current-balance";
import renderLifetimeEarnings from "../../Organisms/Modals/Banking/lifetime-earnings";
import renderAccountDetails from "../../Organisms/Modals/Banking/account-details";

import { useModalContext } from "../../Contexts/ModalContext";

import {
  formatCurrency,
  getAnimateAvailableBalanceFromKey,
  isSVDApproved,
  LocalStorage,
} from "../../Utils/helpers";
import { maskAccountNumber } from "../../Utils/pii";

import { Help, Eye } from "../../Assets/Icons";

export default function BankAccountInformation({
  currentBalance,
  lifetimeEarnings,
  availableBalance,
  stakeDebit = {},
  onViewActivity,
  isWallet = false,
}) {
  const modalContext = useModalContext();

  const IsSVDApproved = useMemo(() => {
    return isSVDApproved(stakeDebit?.data);
  }, [stakeDebit?.data]);

  const animateAvailableBalanceFrom = useMemo(
    () => parseFloat(LocalStorage.read(getAnimateAvailableBalanceFromKey()) ?? 0),
    []
  );

  useEffect(() => {
    const timeout = setTimeout(
      () =>
        LocalStorage.write(
          getAnimateAvailableBalanceFromKey(),
          IsSVDApproved ? availableBalance / 100 : currentBalance
        ),
      2000
    );
    return () => clearTimeout(timeout);
  }, [IsSVDApproved, availableBalance, currentBalance]);

  const accountNumber = useMemo(() => {
    const fullNumber = stakeDebit?.data?.data?.account_number || "XXXXXXXXXXXX";
    return maskAccountNumber(fullNumber, { wrap: false });
  }, [stakeDebit?.data?.data?.account_number]);

  const routingNumber = useMemo(() => {
    return stakeDebit?.data?.data?.routing_number ?? "012341239";
  }, [stakeDebit?.data?.data?.routing_number]);

  return (
    <div className={`banking-account-info${isWallet ? " wallet" : ""}`}>
      <div className="balance-top">
        {IsSVDApproved && (
          <div className="balance-status">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M7 1L2.5 3v3.5c0 3.08 1.92 5.97 4.5 6.5 2.58-.53 4.5-3.42 4.5-6.5V3L7 1z" fill="#2A7F52" stroke="#2A7F52" strokeWidth="0.8" />
              <path d="M5 7l1.5 1.5L9.5 5" stroke="#54FF99" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span>Account Secured</span>
          </div>
        )}

        <div className="balance-label">
          Available Balance
          <button className="icon" onClick={renderAvailableBalance.bind(this, modalContext)} aria-label="About available balance">
            <Help />
          </button>
        </div>

        <div className="balance-row">
          <AmountOdometer
            className="balance-amount"
            initialValue={animateAvailableBalanceFrom}
            value={IsSVDApproved ? availableBalance / 100 : currentBalance}
            size="52px"
            fontWeight={600}
            color="#FFFFFF"
          />
          <button className="balance-action-btn" aria-label="View activity" onClick={onViewActivity}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M7.5 15L12.5 10L7.5 5" stroke="var(--text-muted)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>

        {!isWallet && (
          <div className="balance-sub">
            {IsSVDApproved && (
              <div className="balance-sub-lines">
                <span>
                  Current balance: {formatCurrency(currentBalance)}
                  <button className="icon" onClick={renderCurrentBalance.bind(this, modalContext)} aria-label="About current balance">
                    <Help />
                  </button>
                </span>
                <span>
                  All time Cash Back: {formatCurrency(lifetimeEarnings ?? 0)}
                  <button className="icon" onClick={renderLifetimeEarnings.bind(this, modalContext)} aria-label="About lifetime earnings">
                    <Help />
                  </button>
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {IsSVDApproved && !isWallet && (
        <>
          <div className="divider" />
          <div className="balance-acct-row">
            <div className="balance-acct-item">
              <span className="acct-label">Account</span>
              <span className="acct-val">
                {accountNumber}
                <button className="icon" onClick={() => renderAccountDetails(modalContext, stakeDebit?.data, { initialShowAccount: true })} aria-label="Show account details">
                  <Eye />
                </button>
              </span>
            </div>
            <div className="acct-divider" />
            <div className="balance-acct-item" style={{ paddingLeft: 20 }}>
              <span className="acct-label">Routing</span>
              <span className="acct-val">{routingNumber}</span>
            </div>
          </div>
        </>
      )}

      <div className="divider" />
      <div className="balance-security">
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path d="M6 1L2 2.8v2.4c0 2.64 1.71 5.12 4 5.8 2.29-.68 4-3.16 4-5.8V2.8L6 1z" fill="none" stroke="var(--accent)" strokeWidth="0.8" />
        </svg>
        <span>256-bit encryption</span>
      </div>

    </div>
  );
}
